import {
  computePipeFrictionProperties,
  computeRequiredDischargePressure
} from "@/libs/calculations/tubingFrictionLoss";
import {
  getDeviationSurveyPointsBetweenMeasuredDepths,
  getHydrostaticSumForPiping,
  getRowPhiRad
} from "@/libs/calculations/deviationSurvey";
import { Tubing_Flow_Options_Enum } from "gql/api";
import type { Correlations } from "../forms/formTypes";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";
import type { DeviationSurveyEntryRows } from "./calculationTypes";
import type { PipeFrictionProperties } from "@/libs/calculations/tubingFrictionLoss";

export type TubingFrictionParams = {
  correlations: Correlations;
  topTubingInnerDiameterInches: number;
  topTubingRoughnessInches: number;
  topTubingLengthFt: number;
  bottomTubingInnerDiameterInches: number;
  bottomTubingRoughnessInches: number;
  bottomTubingLengthFt: number;
  topOfPumpsTVDFt: number;
  bottomOfPumpsTVDFt: number;
  topTubingDeviationSurveyEntryRows: DeviationSurveyEntryRows;
  fluidPropertiesInsideTubingContext: FluidPropertiesContext;
  surfaceTemperatureF: number;
  bottomholeTemperatureF: number;
};

export const calculateTopTubingFrictionProperties = (
  params: TubingFrictionParams
): PipeFrictionProperties => {
  const topTubingRowData = getDeviationSurveyPointsBetweenMeasuredDepths(
    params.topTubingDeviationSurveyEntryRows,
    0,
    params.topTubingLengthFt
  );
  const topPhiRad = getRowPhiRad(topTubingRowData.data.slice(-1)[0][1]);

  const resolvedTubingFlow =
    params.correlations?.tubing_flow ?? Tubing_Flow_Options_Enum.BeggsBrill;
  const resolvedTubingApplyHazenWilliams =
    params.correlations?.tubing_apply_hazen_williams_correction ?? false;
  const resolvedTubingHazenWilliamsCoefficient =
    params.correlations?.tubing_hazen_williams_coefficient ?? 0;
  const resolvedTubingPalmerCorrection =
    params.correlations?.tubing_palmer_correction ?? false;
  const resolvedTubingGriffithWallis =
    params.correlations?.tubing_griffith_wallis ?? false;

  return computePipeFrictionProperties(
    topPhiRad,
    params.surfaceTemperatureF,
    params.bottomholeTemperatureF,
    params.topTubingLengthFt,
    params.topTubingInnerDiameterInches,
    params.topTubingLengthFt,
    params.fluidPropertiesInsideTubingContext,
    resolvedTubingFlow,
    resolvedTubingApplyHazenWilliams,
    resolvedTubingHazenWilliamsCoefficient,
    params.topTubingRoughnessInches,
    resolvedTubingPalmerCorrection,
    resolvedTubingGriffithWallis,
    0,
    0
  );
};

export const calculateBottomTubingFrictionProperties = (
  params: TubingFrictionParams
): PipeFrictionProperties => {
  const pipeTotalLengthFt =
    params.topTubingLengthFt + params.bottomTubingLengthFt;

  const bottomTubingRowData = getDeviationSurveyPointsBetweenMeasuredDepths(
    params.topTubingDeviationSurveyEntryRows,
    params.topTubingLengthFt,
    pipeTotalLengthFt
  );
  const bottomPhiRad = getRowPhiRad(bottomTubingRowData.data.slice(-1)[0][1]);

  const resolvedTubingFlow =
    params.correlations?.tubing_flow ?? Tubing_Flow_Options_Enum.BeggsBrill;
  const resolvedTubingApplyHazenWilliams =
    params.correlations?.tubing_apply_hazen_williams_correction ?? false;
  const resolvedTubingHazenWilliamsCoefficient =
    params.correlations?.tubing_hazen_williams_coefficient ?? 0;
  const resolvedTubingPalmerCorrection =
    params.correlations?.tubing_palmer_correction ?? false;
  const resolvedTubingGriffithWallis =
    params.correlations?.tubing_griffith_wallis ?? false;

  return computePipeFrictionProperties(
    bottomPhiRad,
    params.surfaceTemperatureF,
    params.bottomholeTemperatureF,
    params.bottomTubingLengthFt,
    params.bottomTubingInnerDiameterInches,
    pipeTotalLengthFt,
    params.fluidPropertiesInsideTubingContext,
    resolvedTubingFlow,
    resolvedTubingApplyHazenWilliams,
    resolvedTubingHazenWilliamsCoefficient,
    params.bottomTubingRoughnessInches,
    resolvedTubingPalmerCorrection,
    resolvedTubingGriffithWallis,
    0,
    0
  );
};

export type RequiredDischargePressureParams = {
  correlations: Correlations;
  basicData: {
    topTubingLengthFt: number;
    bottomTubingLengthFt: number;
    bottomTubingPressurePSIA: number;
  };
  fluidPropertiesInsideTubingContext: FluidPropertiesContext;
  tubingRowData: ReturnType<
    typeof getDeviationSurveyPointsBetweenMeasuredDepths
  >;
  tubingFrictionLossPSIA: number;
  frictionProperties: PipeFrictionProperties;
};

export const calculateRequiredDischargePressure = (
  params: RequiredDischargePressureParams
): number => {
  const hydrostaticSum = getHydrostaticSumForPiping(params.tubingRowData);
  const resolvedTubingFlow =
    params.correlations?.tubing_flow ?? Tubing_Flow_Options_Enum.BeggsBrill;

  return computeRequiredDischargePressure(
    resolvedTubingFlow,
    params.fluidPropertiesInsideTubingContext.fluidFrictionProperties
      .flowRegime,
    params.basicData.bottomTubingPressurePSIA,
    hydrostaticSum,
    params.tubingFrictionLossPSIA,
    params.frictionProperties
  );
};
